import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clientsApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Search, Building2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const FOX_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/28ykjv9m_Transparent%20BG%20Fox.png';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    industry: '',
    status: 'active',
    priority: 'medium',
    health_status: 'healthy',
    description: ''
  });

  const fetchClients = async () => {
    try {
      const response = await clientsApi.getAll();
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      await clientsApi.create(newClient);
      toast.success('Client created successfully!');
      setDialogOpen(false);
      setNewClient({ name: '', industry: '', status: 'active', priority: 'medium', health_status: 'healthy', description: '' });
      fetchClients();
    } catch (error) {
      toast.error('Failed to create client');
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(search.toLowerCase()) ||
    client.industry?.toLowerCase().includes(search.toLowerCase())
  );

  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy': return 'bg-green-100 text-green-700 border-green-200';
      case 'attention': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = ['#106EFD', '#40CFDE', '#37C563', '#FF801D', '#E171EA', '#FFD166'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">Clients</h1>
          <p className="text-navy/60 mt-1">Manage your client workspaces</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" data-testid="add-client-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-['Amaranth']">Add New Client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClient} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Client Name *</Label>
                <Input
                  id="name"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  placeholder="Enter client name"
                  required
                  data-testid="client-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <Input
                  id="industry"
                  value={newClient.industry}
                  onChange={(e) => setNewClient({ ...newClient, industry: e.target.value })}
                  placeholder="e.g., Fintech, Banking"
                  data-testid="client-industry-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={newClient.priority} 
                    onValueChange={(value) => setNewClient({ ...newClient, priority: value })}
                  >
                    <SelectTrigger data-testid="client-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Health Status</Label>
                  <Select 
                    value={newClient.health_status} 
                    onValueChange={(value) => setNewClient({ ...newClient, health_status: value })}
                  >
                    <SelectTrigger data-testid="client-health-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="healthy">Healthy</SelectItem>
                      <SelectItem value="attention">Needs Attention</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={newClient.description}
                  onChange={(e) => setNewClient({ ...newClient, description: e.target.value })}
                  placeholder="Brief description"
                  data-testid="client-description-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90" data-testid="create-client-btn">
                  Create Client
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
        <Input
          placeholder="Search clients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="client-search-input"
        />
      </div>

      {/* Clients grid */}
      {filteredClients.length === 0 ? (
        <div className="empty-state py-16">
          <img src={FOX_MASCOT} alt="Fox mascot" className="w-32 h-32 mb-4" />
          <h3 className="text-xl font-['Amaranth'] text-[#1A2B5E] mb-2">No clients found</h3>
          <p className="text-navy/60 mb-4">
            {search ? 'Try a different search term' : 'Get started by adding your first client'}
          </p>
          {!search && (
            <Button 
              className="bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <Link key={client.id} to={`/clients/${client.id}`}>
              <Card className="bg-white border-navy/10 card-hover h-full" data-testid={`client-card-${client.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${getAvatarColor(client.name)}20` }}
                    >
                      <span 
                        className="font-bold text-sm"
                        style={{ color: getAvatarColor(client.name) }}
                      >
                        {getInitials(client.name)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#1A2B5E] truncate">{client.name}</h3>
                      <p className="text-sm text-navy/60 truncate">{client.industry || 'No industry specified'}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    <Badge className={getHealthColor(client.health_status)}>
                      {client.health_status}
                    </Badge>
                    <Badge className={getPriorityColor(client.priority)} variant="outline">
                      {client.priority} priority
                    </Badge>
                  </div>

                  {client.description && (
                    <p className="text-sm text-navy/60 mt-3 line-clamp-2">{client.description}</p>
                  )}

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-navy/5">
                    <span className="text-xs text-navy/40">
                      {client.tags?.length || 0} tags
                    </span>
                    <span className="text-[#106EFD] text-sm flex items-center gap-1">
                      View workspace <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Clients;
